import { useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import Typography from "components/basic/typography";
import { formatDollars } from "pages/projectTools";
import QuoteSection from "../quoteSection";
import { QuoteLineItem } from "../quoteTools";

interface QuoteLineItemsProps {
  items: QuoteLineItem[];
  subtotal: number;
  tax: number;
  total: number;
  taxLabel: string;
}

/**
 * DESCRIPTION / AMOUNT table with item rows and a right-aligned
 * subtotal / tax / total summary. Totals recompute upstream whenever the
 * charger model or add-on selection changes.
 */
const QuoteLineItems: React.FC<QuoteLineItemsProps> = ({
  items,
  subtotal,
  tax,
  total,
  taxLabel,
}) => {
  const { t } = useTranslation("estimator");
  const theme = useTheme();

  const itemRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    paddingBottom: theme.spacing(0.5),
  };

  const summaryStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.common.black}`,
  };

  const summaryRowStyle: React.CSSProperties = {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    minWidth: "220px",
  };

  const summaryLabelStyle: React.CSSProperties = {
    flexGrow: 1,
    textAlign: "right",
    fontSize: "1em",
  };

  const renderSummaryRow = (
    label: string,
    value: number,
    emphasize = false,
  ) => (
    <div style={summaryRowStyle}>
      <Typography variant="body1" style={summaryLabelStyle}>
        {label}
      </Typography>
      <Typography
        variant="body2"
        style={emphasize ? { fontWeight: "bold" } : undefined}
      >
        {formatDollars(value)}
      </Typography>
    </div>
  );

  return (
    <QuoteSection title={t("quote.description")} rightTitle={t("quote.amount")}>
      {items.map((item) => (
        <div key={item.id} style={itemRowStyle}>
          <div style={{ flexGrow: 1 }}>
            <Typography variant="body2">{item.label}</Typography>
          </div>
          <Typography variant="body2">{formatDollars(item.price)}</Typography>
        </div>
      ))}

      <div style={summaryStyle}>
        {renderSummaryRow(t("quote.subtotal"), subtotal)}
        {renderSummaryRow(taxLabel, tax)}
        {renderSummaryRow(t("quote.total"), total, true)}
      </div>
    </QuoteSection>
  );
};

export default QuoteLineItems;
